/**
 * Access by subcompany (feature/acceso-subcompanias). Pure rules for the Users
 * form and for the redirect when a subcompany is not allowed; the back decides
 * (lib/access.js there) and these only shape the screen. Tests:
 * src/lib/__tests__/subcompanyAccess.test.js.
 *
 * The role stays one per company; RoleCompany.subcompanies says where it
 * applies. An empty list means every subcompany ("Todas").
 */

/** The "Todas" option of the selector. Not an id: sent as []. */
export const ALL = 'ALL';

const split = (value) =>
  String(value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/** Selector value (FieldMultiSelect: comma-joined) for the list of a user. */
export const selectionFromList = (list) => (list && list.length ? list.join(',') : ALL);

/**
 * The value after a change in the selector: choosing "Todas" clears the rest,
 * choosing a subcompany drops "Todas", and nothing selected goes back to
 * "Todas" (the default).
 */
export const nextSelection = (prev, next) => {
  const ids = split(next);
  if (!ids.length) return ALL;
  if (ids.includes(ALL) && !split(prev).includes(ALL)) return ALL;
  const rest = ids.filter((id) => id !== ALL);
  return rest.length ? rest.join(',') : ALL;
};

/** The list the back expects: [] = "Todas". */
export const listFromSelection = (selection) => split(selection).filter((id) => id !== ALL);

/**
 * The body of POST/PUT users. Only root or the Administrador send
 * `subcompanies`; for anyone else the back answers 403 if it comes (a user they
 * create inherits their list).
 */
export const userPayload = (user, canAssign) => {
  const { subcompaniesSelection, subcompanies, ...rest } = user || {};
  return canAssign && !isRootUser(user)
    ? { ...rest, subcompanies: listFromSelection(subcompaniesSelection) }
    : rest;
};

/**
 * A root user sees every subcompany and the back does not edit it (users.update
 * filters root: false): the form shows no selector for it.
 */
export const isRootUser = (user) => !!(user && user.root === true);

/**
 * Whether the answer of POST/PUT users has the list that was sent. A back
 * without this feature (or not restarted after changing branch) ignores
 * `subcompanies` and answers 200 without it: the form must say so instead of
 * looking saved and showing "Todas" when reopened.
 */
export const subcompaniesSaved = (body, answeredUser) => {
  if (!body || body.subcompanies === undefined) return true; // nothing was sent
  const saved = answeredUser && answeredUser.company && answeredUser.company.subcompanies;
  if (!Array.isArray(saved)) return false;
  const a = [...body.subcompanies].map(String).sort();
  const b = [...saved].map(String).sort();
  return a.length === b.length && a.every((id, i) => id === b[i]);
};

/**
 * Roles offered in the form: only root or the Administrador may give the
 * Administrador role (default: true). The current role of the user edited is
 * kept, so the select does not show an empty value.
 */
export const assignableRoles = (roles, canAssign, currentRoleId) =>
  canAssign ? roles || [] : (roles || []).filter((r) => !r.default || r.id === currentRoleId);

/**
 * Where to go when the subcompany of the URL is not in the user's list: the
 * same section of the first allowed one (not the same record: its ids belong
 * to the other subcompany). { none: true } when no subcompany is allowed.
 */
export const subcompanyFallback = (pathname, companyId, sid, allowedIds) => {
  const ids = allowedIds || [];
  if (!ids.length) return { none: true };
  const marker = `/companies/${companyId}/subcompanies/${sid}`;
  const at = String(pathname || '').indexOf(marker);
  const base = at >= 0 ? pathname.slice(0, at) : '';
  const after = at >= 0 ? pathname.slice(at + marker.length) : '';
  const section = after.split('/').filter(Boolean)[0] || 'dashboard';
  return { to: `${base}/companies/${companyId}/subcompanies/${ids[0]}/${section}` };
};



// WEBPACK FOOTER //
// src/lib/subcompanyAccess.js